import React, { useState } from "react";
import "./CphereBrandFooter.css";
// The production brain mark. This is the exact asset the Campaign Strategy
// Microsite footer has always shipped — not a re-export, not a variant, not a
// second brain from elsewhere in the design system.
import cphereBrainMark from "../assets/back-icon.gif";

/**
 * CphereBrandFooter — the one Cphere brand footer.
 *
 * This markup was the Campaign Strategy Microsite's inline footer. It was
 * lifted here unchanged so other client-facing surfaces (the public Platform
 * Analytics report, first) can render the APPROVED footer instead of an
 * approximation of it. The microsite renders it with no props at all, and its
 * default output is byte-identical to what it rendered inline before — that
 * equality is pinned in CphereBrandFooter.test.js.
 *
 * The two knobs a surface may turn:
 *
 *   lead / trail   the credit line either side of the canonical separator.
 *                  Defaults are the microsite's. A surface with no trailing
 *                  half (e.g. a report with no campaign bound) passes an
 *                  empty trail and the separator disappears with it, rather
 *                  than dangling next to nothing.
 *
 *   note           one line of surface-specific disclosure rendered beneath
 *                  the brand row, INSIDE the same <footer>. This exists so a
 *                  surface that owes its viewer a legal/scope statement can
 *                  keep it without stacking a second Cphere footer under the
 *                  first one.
 *
 * Everything else — the mark, its size and glow, the typography, the divider,
 * the spacing, the centering, the wrap behavior — is the shared treatment and
 * is not parameterised. That is deliberate.
 */
export const CPHERE_BRAND_FOOTER_LEAD = "Powered by Cphere";
export const CPHERE_BRAND_FOOTER_TRAIL = "Agentic Media Plan";

// The lead half of the credit on a PUBLIC REPORTING surface. The microsite
// credits the plan it powers; a shared report credits the reporting itself,
// by name. It lives here beside the microsite's own defaults so the two
// leads are read together and a second public-reporting surface has one
// string to render rather than a literal to re-type.
export const PUBLIC_REPORT_FOOTER_LEAD = "Cortex Reporting";

export default function CphereBrandFooter({
  logoSrc = cphereBrainMark,
  lead = CPHERE_BRAND_FOOTER_LEAD,
  trail = CPHERE_BRAND_FOOTER_TRAIL,
  ariaLabel,
  className = "",
  textClassName = "",
  note = null,
}) {
  // A brand mark that fails to load must not leave a hole where the footer
  // is: the credit line stands on its own text-only.
  const [logoUnavailable, setLogoUnavailable] = useState(false);
  const showLogo = Boolean(logoSrc) && !logoUnavailable;

  const trailing = typeof trail === "string" ? trail.trim() : trail;
  const label = ariaLabel || [lead, trailing].filter(Boolean).join(" ");

  return (
    <footer
      className={
        className
          ? `campaign-strategy-microsite-brand-footer ${className}`
          : "campaign-strategy-microsite-brand-footer"
      }
      aria-label={label}
    >
      <div className="campaign-strategy-microsite-brand-footer-inner">
        {showLogo ? (
          <img
            className="campaign-strategy-microsite-brand-footer-mark"
            src={logoSrc}
            alt=""
            aria-hidden="true"
            onError={() => setLogoUnavailable(true)}
          />
        ) : null}
        <span
          className={
            textClassName
              ? `campaign-strategy-microsite-brand-footer-text ${textClassName}`
              : "campaign-strategy-microsite-brand-footer-text"
          }
        >
          {lead}
          {trailing ? (
            <>
              {" "}
              <span
                className="campaign-strategy-microsite-brand-footer-separator"
                aria-hidden="true"
              >
                |
              </span>{" "}
              {trailing}
            </>
          ) : null}
        </span>
      </div>
      {note}
    </footer>
  );
}
